/**
 * /app/settings/config-financiera — SCRUM-550
 * 3 ABMs: Cuentas bancarias, Métodos de pago y Dispositivos de cobro.
 *
 * Alcance: solo configuración. NO cablea estos catálogos al checkout
 * (POS/online) — eso es trabajo de historias futuras.
 */
import { useCallback, useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, Trash2, RefreshCw, Pencil, X, Landmark, CreditCard, Cpu } from "lucide-react";
import { DataTable, CrudEmptyState, Loading, Panel, CrudShellHeader } from "@/components/ds";
import type { DataTableColumn } from "@/components/ds";
import { ClinicalPageShell } from "@/components/clinical";
import { SettingsSectionBack } from "@/components/settings/SettingsSectionHub";
import { apiGetJson, apiSendJson, apiDelete } from "@/lib/sm-api";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { useTenantContextSafe } from "@/lib/tenant-context";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/app/settings/config-financiera")({
  component: ConfigFinancieraPage,
  head: () => ({ meta: [{ title: "Cuentas y medios de cobro — Structura Market" }] }),
});

type SubTab = "cuentas" | "metodos" | "dispositivos";

function ConfigFinancieraPage() {
  const tid = useWorkspaceTenantId();
  const ctx = useTenantContextSafe();
  const navigate = useNavigate();
  const entitled = ctx?.productFlags?.payment_config === true;
  const [tab, setTab] = useState<SubTab>("cuentas");

  if (!entitled) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        Requiere la feature payment_config en tu plan.
      </div>
    );
  }

  const TABS: { id: SubTab; label: string; icon: typeof Landmark }[] = [
    { id: "cuentas", label: "Cuentas bancarias", icon: Landmark },
    { id: "metodos", label: "Métodos de pago", icon: CreditCard },
    { id: "dispositivos", label: "Dispositivos de cobro", icon: Cpu },
  ];

  return (
    <div>
      <CrudShellHeader title="Cuentas y medios de cobro" />
      <ClinicalPageShell offset="none">
        <div className="mx-auto w-full max-w-4xl space-y-4 p-4">
          <SettingsSectionBack
            title="Cuentas y medios de cobro"
            description="Configurá dónde se acreditan tus ventas, qué comisión pagás y con qué dispositivo cobrás"
            onBack={() => void navigate({ to: "/app/settings", search: {} as never })}
          />

          <div className="flex gap-1 border-b border-border/40">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={cn(
                  "flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors",
                  tab === t.id
                    ? "border-primary text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                )}
              >
                <t.icon className="h-3.5 w-3.5" />
                {t.label}
              </button>
            ))}
          </div>

          <div className="pb-12">
            {tid && tab === "cuentas" && <BankAccountsSection tid={tid} />}
            {tid && tab === "dispositivos" && <PaymentDevicesSection tid={tid} />}
            {tid && tab === "metodos" && <PaymentMethodsSection tid={tid} />}
          </div>
        </div>
      </ClinicalPageShell>
    </div>
  );
}

// ── Cuentas bancarias ────────────────────────────────────────────────────────

interface BankAccount {
  id: string;
  name: string;
  financialEntity: string;
  balance: number;
  isActive: boolean;
}

const FINANCIAL_ENTITY_OPTIONS = [
  "Banco Galicia", "Banco Nación", "Banco Santander", "Banco BBVA", "Banco Macro",
  "Banco Credicoop", "Banco Ciudad", "Banco Provincia", "Banco Supervielle",
  "Banco Patagonia", "Banco ICBC", "Banco ROELA", "Mercado Pago", "Ualá",
  "Naranja X", "Brubank", "Personal Pay", "Otra",
] as const;

function BankAccountsSection({ tid }: { tid: string }) {
  const [items, setItems] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [entity, setEntity] = useState<string>(FINANCIAL_ENTITY_OPTIONS[0]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiGetJson<{ bankAccounts: BankAccount[] }>("/api/settings/bank-accounts", tid);
      setItems(d.bankAccounts ?? []);
    } finally {
      setLoading(false);
    }
  }, [tid]);

  useEffect(() => { if (tid) void load(); }, [tid, load]);

  const resetForm = () => { setName(""); setEntity(FINANCIAL_ENTITY_OPTIONS[0]); setEditingId(null); setShowForm(false); };

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      if (editingId) {
        await apiSendJson(`/api/settings/bank-accounts?id=${editingId}`, "PATCH", { name, financialEntity: entity }, tid);
        toast.success("Cuenta actualizada");
      } else {
        await apiSendJson("/api/settings/bank-accounts", "POST", { name, financialEntity: entity }, tid);
        toast.success("Cuenta creada");
      }
      resetForm();
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (a: BankAccount) => {
    setEditingId(a.id); setName(a.name); setEntity(a.financialEntity); setShowForm(true);
  };

  const toggle = async (a: BankAccount) => {
    try {
      await apiSendJson(`/api/settings/bank-accounts?id=${a.id}`, "PATCH", { isActive: !a.isActive }, tid);
      await load();
    } catch { toast.error("Error"); }
  };

  const del = async (id: string) => {
    try {
      await apiDelete(`/api/settings/bank-accounts?id=${id}`, tid);
      await load();
      toast.success("Eliminada");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    }
  };

  const columns: DataTableColumn<BankAccount>[] = [
    {
      key: "cuenta",
      header: "Cuenta",
      render: (a) => (
        <div className={a.isActive ? undefined : "opacity-60"}>
          <p className="text-sm font-medium">{a.name}</p>
          <p className="text-xs text-muted-foreground">{a.financialEntity}</p>
        </div>
      ),
    },
    {
      key: "saldo",
      header: "Saldo",
      render: (a) => <span className="text-sm tabular-nums">${a.balance.toFixed(2)}</span>,
    },
    {
      key: "estado",
      header: "Estado",
      render: (a) => <Badge variant={a.isActive ? "default" : "secondary"}>{a.isActive ? "Activa" : "Inactiva"}</Badge>,
    },
    {
      key: "acciones",
      header: "Acciones",
      className: "text-right",
      render: (a) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => startEdit(a)} title="Editar"><Pencil className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="sm" onClick={() => toggle(a)} title="Activar/desactivar"><RefreshCw className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="sm" onClick={() => del(a.id)} title="Eliminar"><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <Panel
        title="Cuentas bancarias"
        description="Dónde se acreditan o mantienen los fondos del negocio"
        actions={
          <Button size="sm" onClick={() => { setShowForm((v) => !v); if (editingId) resetForm(); }} variant={showForm && !editingId ? "secondary" : "default"}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Nueva cuenta
          </Button>
        }
      >
        {loading ? <Loading variant="list" rows={3} /> : (
          <DataTable columns={columns} rows={items} getRowKey={(a) => a.id} emptyState={<CrudEmptyState title="Sin cuentas todavía" />} />
        )}
      </Panel>

      {showForm && (
        <Panel
          title={editingId ? "Editar cuenta" : "Nueva cuenta"}
          actions={<button type="button" onClick={resetForm} className="text-muted-foreground hover:text-foreground" title="Cancelar"><X className="h-4 w-4" /></button>}
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-xs">Nombre *</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej: Cuenta operativa Galicia" />
            </div>
            <div>
              <Label className="text-xs">Entidad financiera *</Label>
              <select
                value={entity}
                onChange={(e) => setEntity(e.target.value)}
                className="mt-0 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
              >
                {FINANCIAL_ENTITY_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>
          <div className="flex justify-end pt-3">
            <Button size="sm" disabled={saving || !name.trim()} onClick={save}>
              {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
              {editingId ? "Guardar cambios" : "Crear cuenta"}
            </Button>
          </div>
        </Panel>
      )}
    </div>
  );
}

// ── Dispositivos de cobro ────────────────────────────────────────────────────

type DeviceCategory = "terminal_fisica" | "integracion_api";
interface PaymentDevice { id: string; name: string; category: DeviceCategory; isActive: boolean }

const DEVICE_CATEGORY_LABELS: Record<DeviceCategory, string> = {
  terminal_fisica: "Terminal física (Caja)",
  integracion_api: "Integración API (Tienda online)",
};

function PaymentDevicesSection({ tid }: { tid: string }) {
  const [items, setItems] = useState<PaymentDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [category, setCategory] = useState<DeviceCategory>("terminal_fisica");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiGetJson<{ devices: PaymentDevice[] }>("/api/settings/payment-devices", tid);
      setItems(d.devices ?? []);
    } finally { setLoading(false); }
  }, [tid]);

  useEffect(() => { if (tid) void load(); }, [tid, load]);

  const resetForm = () => { setName(""); setCategory("terminal_fisica"); setEditingId(null); setShowForm(false); };

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      if (editingId) {
        await apiSendJson(`/api/settings/payment-devices?id=${editingId}`, "PATCH", { name, category }, tid);
        toast.success("Dispositivo actualizado");
      } else {
        await apiSendJson("/api/settings/payment-devices", "POST", { name, category }, tid);
        toast.success("Dispositivo creado");
      }
      resetForm();
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    } finally { setSaving(false); }
  };

  const startEdit = (d: PaymentDevice) => { setEditingId(d.id); setName(d.name); setCategory(d.category); setShowForm(true); };

  const toggle = async (d: PaymentDevice) => {
    try {
      await apiSendJson(`/api/settings/payment-devices?id=${d.id}`, "PATCH", { isActive: !d.isActive }, tid);
      await load();
    } catch { toast.error("Error"); }
  };

  const del = async (id: string) => {
    try {
      await apiDelete(`/api/settings/payment-devices?id=${id}`, tid);
      await load();
      toast.success("Eliminado");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    }
  };

  const columns: DataTableColumn<PaymentDevice>[] = [
    {
      key: "dispositivo",
      header: "Dispositivo",
      render: (d) => (
        <div className={d.isActive ? undefined : "opacity-60"}>
          <p className="text-sm font-medium">{d.name}</p>
          <p className="text-xs text-muted-foreground">{DEVICE_CATEGORY_LABELS[d.category]}</p>
        </div>
      ),
    },
    {
      key: "estado",
      header: "Estado",
      render: (d) => <Badge variant={d.isActive ? "default" : "secondary"}>{d.isActive ? "Activo" : "Inactivo"}</Badge>,
    },
    {
      key: "acciones",
      header: "Acciones",
      className: "text-right",
      render: (d) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => startEdit(d)} title="Editar"><Pencil className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="sm" onClick={() => toggle(d)} title="Activar/desactivar"><RefreshCw className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="sm" onClick={() => del(d.id)} title="Eliminar"><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <Panel
        title="Dispositivos de cobro"
        description="Terminales físicas (Caja) e integraciones API (Tienda online). La configuración específica de cada marca se hace en historias independientes."
        actions={
          <Button size="sm" onClick={() => { setShowForm((v) => !v); if (editingId) resetForm(); }} variant={showForm && !editingId ? "secondary" : "default"}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Nuevo dispositivo
          </Button>
        }
      >
        {loading ? <Loading variant="list" rows={3} /> : (
          <DataTable columns={columns} rows={items} getRowKey={(d) => d.id} emptyState={<CrudEmptyState title="Sin dispositivos todavía" />} />
        )}
      </Panel>

      {showForm && (
        <Panel
          title={editingId ? "Editar dispositivo" : "Nuevo dispositivo"}
          actions={<button type="button" onClick={resetForm} className="text-muted-foreground hover:text-foreground" title="Cancelar"><X className="h-4 w-4" /></button>}
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-xs">Nombre *</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej: Payway Smart mostrador 1" />
            </div>
            <div>
              <Label className="text-xs">Categoría *</Label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as DeviceCategory)}
                className="mt-0 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
              >
                {(Object.keys(DEVICE_CATEGORY_LABELS) as DeviceCategory[]).map((c) => (
                  <option key={c} value={c}>{DEVICE_CATEGORY_LABELS[c]}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end pt-3">
            <Button size="sm" disabled={saving || !name.trim()} onClick={save}>
              {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
              {editingId ? "Guardar cambios" : "Crear dispositivo"}
            </Button>
          </div>
        </Panel>
      )}
    </div>
  );
}

// ── Métodos de pago ──────────────────────────────────────────────────────────

type StandardMethod =
  | "efectivo" | "cheque" | "echeq" | "transferencia_bancaria"
  | "tarjeta_debito" | "tarjeta_credito" | "qr" | "gift_card" | "puntos_fidelidad";
type Channel = "caja" | "tienda_online";

const STANDARD_METHOD_LABELS: Record<StandardMethod, string> = {
  efectivo: "Efectivo",
  cheque: "Cheque",
  echeq: "e-Cheq",
  transferencia_bancaria: "Transferencia bancaria",
  tarjeta_debito: "Tarjeta de débito",
  tarjeta_credito: "Tarjeta de crédito",
  qr: "QR",
  gift_card: "Gift card",
  puntos_fidelidad: "Puntos fidelidad",
};

const METHODS_SIN_ACREDITACION: StandardMethod[] = ["efectivo", "gift_card", "puntos_fidelidad"];

const CHANNEL_LABELS: Record<Channel, string> = { caja: "Caja", tienda_online: "Tienda online" };

interface PaymentMethodConfig {
  id: string;
  name: string;
  standardMethod: StandardMethod;
  channel: Channel;
  bankAccountId: string | null;
  bankAccountName?: string | null;
  settlementDays: number | null;
  commissionPct: number | null;
  deviceId: string | null;
  deviceName?: string | null;
  isActive: boolean;
}

const EMPTY_METHOD_FORM = {
  name: "",
  standardMethod: "efectivo" as StandardMethod,
  channel: "caja" as Channel,
  bankAccountId: "",
  settlementDays: "",
  commissionPct: "",
  deviceId: "",
};

function PaymentMethodsSection({ tid }: { tid: string }) {
  const [items, setItems] = useState<PaymentMethodConfig[]>([]);
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [devices, setDevices] = useState<PaymentDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_METHOD_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, a, d] = await Promise.all([
        apiGetJson<{ paymentMethods: PaymentMethodConfig[] }>("/api/settings/payment-methods", tid),
        apiGetJson<{ bankAccounts: BankAccount[] }>("/api/settings/bank-accounts", tid),
        apiGetJson<{ devices: PaymentDevice[] }>("/api/settings/payment-devices", tid),
      ]);
      setItems(m.paymentMethods ?? []);
      setAccounts(a.bankAccounts ?? []);
      setDevices(d.devices ?? []);
    } finally { setLoading(false); }
  }, [tid]);

  useEffect(() => { if (tid) void load(); }, [tid, load]);

  const requiresAcreditacion = !METHODS_SIN_ACREDITACION.includes(form.standardMethod);
  const compatibleDevices = devices.filter((d) => d.isActive && (form.channel === "caja" ? d.category === "terminal_fisica" : d.category === "integracion_api"));
  const activeAccounts = accounts.filter((a) => a.isActive);

  const resetForm = () => { setForm(EMPTY_METHOD_FORM); setEditingId(null); setShowForm(false); };

  const save = async () => {
    if (!form.name.trim()) return;
    const payload: Record<string, unknown> = {
      name: form.name,
      standardMethod: form.standardMethod,
      channel: form.channel,
      bankAccountId: requiresAcreditacion ? (form.bankAccountId || null) : null,
      settlementDays: requiresAcreditacion && form.settlementDays !== "" ? Number(form.settlementDays) : null,
      commissionPct: requiresAcreditacion && form.commissionPct !== "" ? Number(form.commissionPct) : null,
      deviceId: form.deviceId || null,
    };
    setSaving(true);
    try {
      if (editingId) {
        await apiSendJson(`/api/settings/payment-methods?id=${editingId}`, "PATCH", payload, tid);
        toast.success("Método actualizado");
      } else {
        await apiSendJson("/api/settings/payment-methods", "POST", payload, tid);
        toast.success("Método creado");
      }
      resetForm();
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    } finally { setSaving(false); }
  };

  const startEdit = (m: PaymentMethodConfig) => {
    setEditingId(m.id);
    setForm({
      name: m.name,
      standardMethod: m.standardMethod,
      channel: m.channel,
      bankAccountId: m.bankAccountId ?? "",
      settlementDays: m.settlementDays != null ? String(m.settlementDays) : "",
      commissionPct: m.commissionPct != null ? String(m.commissionPct) : "",
      deviceId: m.deviceId ?? "",
    });
    setShowForm(true);
  };

  const toggle = async (m: PaymentMethodConfig) => {
    try {
      await apiSendJson(`/api/settings/payment-methods?id=${m.id}`, "PATCH", { isActive: !m.isActive }, tid);
      await load();
    } catch { toast.error("Error"); }
  };

  const del = async (id: string) => {
    try {
      await apiDelete(`/api/settings/payment-methods?id=${id}`, tid);
      await load();
      toast.success("Eliminado");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error");
    }
  };

  const columns: DataTableColumn<PaymentMethodConfig>[] = [
    {
      key: "metodo",
      header: "Método",
      render: (m) => (
        <div className={m.isActive ? undefined : "opacity-60"}>
          <p className="text-sm font-medium">{m.name}</p>
          <p className="text-xs text-muted-foreground">
            {STANDARD_METHOD_LABELS[m.standardMethod]} · {CHANNEL_LABELS[m.channel]}
          </p>
        </div>
      ),
    },
    {
      key: "financiero",
      header: "Acreditación",
      render: (m) =>
        METHODS_SIN_ACREDITACION.includes(m.standardMethod) ? (
          <span className="text-xs text-muted-foreground">No corresponde</span>
        ) : (
          <div className="text-xs text-muted-foreground">
            <p>{m.bankAccountName ?? "—"}</p>
            <p>{m.settlementDays ?? 0}d · {m.commissionPct ?? 0}%</p>
          </div>
        ),
    },
    {
      key: "dispositivo",
      header: "Dispositivo",
      render: (m) => <span className="text-xs text-muted-foreground">{m.deviceName ?? "—"}</span>,
    },
    {
      key: "estado",
      header: "Estado",
      render: (m) => <Badge variant={m.isActive ? "default" : "secondary"}>{m.isActive ? "Activo" : "Inactivo"}</Badge>,
    },
    {
      key: "acciones",
      header: "Acciones",
      className: "text-right",
      render: (m) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => startEdit(m)} title="Editar"><Pencil className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="sm" onClick={() => toggle(m)} title="Activar/desactivar"><RefreshCw className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="sm" onClick={() => del(m.id)} title="Eliminar"><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <Panel
        title="Métodos de pago"
        description="Configuración específica de cobro por canal, con su condición financiera"
        actions={
          <Button size="sm" onClick={() => { setShowForm((v) => !v); if (editingId) resetForm(); }} variant={showForm && !editingId ? "secondary" : "default"}>
            <Plus className="h-3.5 w-3.5 mr-1" /> Nuevo método
          </Button>
        }
      >
        {loading ? <Loading variant="list" rows={3} /> : (
          <DataTable columns={columns} rows={items} getRowKey={(m) => m.id} emptyState={<CrudEmptyState title="Sin métodos de pago todavía" />} />
        )}
      </Panel>

      {showForm && (
        <Panel
          title={editingId ? "Editar método" : "Nuevo método"}
          actions={<button type="button" onClick={resetForm} className="text-muted-foreground hover:text-foreground" title="Cancelar"><X className="h-4 w-4" /></button>}
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label className="text-xs">Nombre *</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Ej: Crédito Payway 1 pago" />
            </div>
            <div>
              <Label className="text-xs">Método estándar *</Label>
              <select
                value={form.standardMethod}
                onChange={(e) => setForm((f) => ({ ...f, standardMethod: e.target.value as StandardMethod }))}
                className="mt-0 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
              >
                {(Object.keys(STANDARD_METHOD_LABELS) as StandardMethod[]).map((k) => (
                  <option key={k} value={k}>{STANDARD_METHOD_LABELS[k]}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Canal *</Label>
              <select
                value={form.channel}
                onChange={(e) => setForm((f) => ({ ...f, channel: e.target.value as Channel, deviceId: "" }))}
                className="mt-0 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
              >
                {(Object.keys(CHANNEL_LABELS) as Channel[]).map((k) => (
                  <option key={k} value={k}>{CHANNEL_LABELS[k]}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs">Dispositivo de cobro (opcional)</Label>
              <select
                value={form.deviceId}
                onChange={(e) => setForm((f) => ({ ...f, deviceId: e.target.value }))}
                className="mt-0 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
              >
                <option value="">Sin dispositivo</option>
                {compatibleDevices.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>

            {requiresAcreditacion ? (
              <>
                <div>
                  <Label className="text-xs">Cuenta de depósito *</Label>
                  <select
                    value={form.bankAccountId}
                    onChange={(e) => setForm((f) => ({ ...f, bankAccountId: e.target.value }))}
                    className="mt-0 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                  >
                    <option value="">Seleccionar…</option>
                    {activeAccounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Plazo de acreditación (días) *</Label>
                  <Input type="number" min={0} value={form.settlementDays} onChange={(e) => setForm((f) => ({ ...f, settlementDays: e.target.value }))} placeholder="0" />
                </div>
                <div>
                  <Label className="text-xs">Comisión (%) *</Label>
                  <Input type="number" min={0} max={100} step="0.01" value={form.commissionPct} onChange={(e) => setForm((f) => ({ ...f, commissionPct: e.target.value }))} placeholder="0" />
                </div>
              </>
            ) : (
              <div className="sm:col-span-2 text-xs text-muted-foreground rounded-lg bg-muted/40 px-3 py-2">
                {STANDARD_METHOD_LABELS[form.standardMethod]} no tiene acreditación financiera propia — no corresponde cuenta, plazo ni comisión.
              </div>
            )}
          </div>
          <div className="flex justify-end pt-3">
            <Button size="sm" disabled={saving || !form.name.trim()} onClick={save}>
              {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
              {editingId ? "Guardar cambios" : "Crear método"}
            </Button>
          </div>
        </Panel>
      )}
    </div>
  );
}
